import React, { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { setPageTitle } from "../../../Store/modules/mms/actions";
import MmsApiService from "Services/MMS/mms-api.service";
import {
  CircularProgress,
  Fade,
  Button,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
} from "@material-ui/core";
import AlertToastr from "utils/alert";


const getOperatorSymbol = (operator) => {
  switch (operator) {
    case 'smallerThan':
      return '<';
    case 'biggerThan':
      return '>';
    case 'equals':
      return '=';
    default:
      return operator; 
  }
};

const AssetTypeDetails = (props) => {
  const { setPageTitle } = props;
  const { assetTypeId } = useParams();
  const history = useHistory();
  const [assetType, setAssetType] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metricsValues, setMetricsValues] = useState({});

  const fetchAssetTypeDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await MmsApiService.getSetpointsByAseetType(assetTypeId);
    
      const assetTypeData = response.data;
      setAssetType(assetTypeData);
    } catch (error) {
      setError("Error loading asset type details");
      AlertToastr.showErrorAlert("Error loading asset type details");
    } finally {
      setIsLoading(false);
    }
  }, [assetTypeId]);

  useEffect(() => {
    setPageTitle("Loading Asset Type...");
  }, [setPageTitle]);

  useEffect(() => {
    if (assetType) {
      setPageTitle(`Asset Type: ${assetType.assetTypeName}`);
    } else if (error) {
      setPageTitle("Asset Type Not Found");
    }
  }, [assetType, error, setPageTitle]);

  useEffect(() => {
    fetchAssetTypeDetails();
  }, [fetchAssetTypeDetails]);

  const handleBackClick = () => {
    history.push("/asset-types");
  };

  const handleMetricChange = (componentTypeId, manufacturerId, modelId, metricIndex, value) => {
    const key = `${componentTypeId}-${manufacturerId}-${modelId}-${metricIndex}`;
    setMetricsValues(prev => {
      const newValues = {
        ...prev,
        [key]: value
      };
      return newValues;
    });
  };

  const handleSaveMetrics = async () => {
    try {
      setIsLoading(true);
      
      const changedRows = [];
      
      assetType.componentTypeTables.forEach(componentTypeTable => {
        if (componentTypeTable.rows && componentTypeTable.rows.length > 0) {
          const availableMetrics = componentTypeTable.availableMetrics || [];
          
          componentTypeTable.rows.forEach(row => {
            availableMetrics.forEach((metric, metricIndex) => {
              const key = `${componentTypeTable.componentTypeId}-${row.manufacturerId}-${row.modelId}-${metricIndex}`;
              const originalValue = row.setpointValues && row.setpointValues[metric.id] 
                ? row.setpointValues[metric.id].value 
                : "";
              const currentValue = metricsValues[key];
              
              if (metricsValues[key] !== undefined && currentValue !== originalValue) {
                changedRows.push({
                  componentTypeId: componentTypeTable.componentTypeId,
                  manufacturerId: row.manufacturerId,
                  modelId: row.modelId,
                  setpointValues: [{
                    metricId: metric.id,
                    value: parseFloat(currentValue) || 0
                  }]
                });
              }
            });
          });
        }
      });
      
      if (changedRows.length === 0) {
        AlertToastr.showAlert("No changes to save.");
        return;
      }
      
      await MmsApiService.updateSetpointsByAseetType(assetTypeId, changedRows);
      AlertToastr.showAlert("All metrics saved successfully!");

      await fetchAssetTypeDetails();
      
    } catch (error) {
      AlertToastr.showErrorAlert("Error saving metrics. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const createMetricsTable = (componentTypeTable) => {
    const rows = [];
    
    if (!componentTypeTable.rows || componentTypeTable.rows.length === 0) {
      return (
        <Card style={{ marginBottom: "24px" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {componentTypeTable.componentTypeName} Setpoints
            </Typography>
            <Typography variant="body2" color="textSecondary">
              No data available for this component type.
            </Typography>
          </CardContent>
        </Card>
      );
    }

    componentTypeTable.rows.forEach(row => {
      rows.push({
        id: `${row.manufacturerId}-${row.modelId}`,
        componentType: componentTypeTable.componentTypeName,
        manufacturer: row.manufacturerName,
        manufacturerId: row.manufacturerId,
        model: row.modelName,
        modelId: row.modelId,
        fullModelName: row.displayName,
        setpointValues: row.setpointValues
      });
    });
   
    const availableMetrics = componentTypeTable.availableMetrics || [];

    return (
      <Card style={{ marginBottom: "24px", boxShadow: "none" }}>
        <CardContent>
          <TableContainer component={Paper} style={{ marginTop: "16px", overflowX: "auto", boxShadow: "none" }}>
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell style={{ fontWeight: "bold", minWidth: "200px", border: "1px solid #e0e0e0" }}>
                    {componentTypeTable.componentTypeName}
                  </TableCell>
                  {availableMetrics.map((metric, index) => (
                    <TableCell 
                      key={index} 
                      style={{ 
                        fontWeight: "bold", 
                        textAlign: "center",
                        minWidth: "150px",
                        maxWidth: "150px",
                        fontSize: "11px",
                        lineHeight: "1.2",
                        border: "1px solid #e0e0e0",
                        padding: "8px 4px"
                      }}
                    >
                      {/* {`${metric.name} ${getOperatorSymbol(metric.condition)} ${metric.conditionValue}`} */}
                      {metric.displayName}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={row.id} style={{ borderBottom: "1px solid #e0e0e0" }}>
                    <TableCell style={{ verticalAlign: "middle", border: "1px solid #e0e0e0", padding: "12px" }}>
                      <div>
                        <Typography variant="subtitle2"  style={{ fontSize: "12px", fontWeight: "bold" }}>
                          {row.fullModelName}
                        </Typography>
                      </div>
                    </TableCell>
                    {availableMetrics.map((metric, metricIndex) => {
                      const key = `${componentTypeTable.componentTypeId}-${row.manufacturerId}-${row.modelId}-${metricIndex}`;
                      const currentValue = metricsValues[key] !== undefined 
                        ? metricsValues[key] 
                        : (row.setpointValues && row.setpointValues[metric.id] 
                          ? row.setpointValues[metric.id].value 
                          : "");
                      
                      return (
                        <TableCell key={key} style={{ textAlign: "center", border: "1px solid #e0e0e0", padding: "8px" }}>
                          <TextField
                            key={key}
                            variant="outlined"
                            size="small"
                            style={{ width: "70px" }}
                            placeholder=""
                            value={currentValue}
                            onChange={(e) => handleMetricChange(componentTypeTable.componentTypeId, row.manufacturerId, row.modelId, metricIndex, e.target.value)}
                            inputProps={{
                              style: { 
                                textAlign: "center", 
                                fontSize: "12px",
                                padding: "4px 8px"
                              }
                            }}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };


  if (isLoading) {
    return (
      <div className="pmPageHolder" style={{ 
        height: "100vh", 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center" 
      }}>
        <Fade in={isLoading} unmountOnExit>
          <CircularProgress />
        </Fade>
      </div>
    );
  }

  if (error || !assetType) {
    return (
      <div className="pmPageHolder">
        <div className="pmPageBlock">
          <div className="pmPageBlockTitle">Asset Type Not Found</div>
          <Button
            onClick={handleBackClick}
            variant="outlined"
          >
            Back to Asset Types
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="pmPageHolder">
      <div className="pmPageBlock">
        <div className="pmPageBlockTitle" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <span 
              onClick={handleBackClick}
              style={{ 
                cursor: "pointer",
                marginRight: "8px",
                fontSize: "16px"
              }}
            >
              Asset Types
            </span>
            <span style={{ marginRight: "8px", fontSize: "16px" }}>/</span>
            <span style={{ fontSize: "16px" }}>{assetType.assetTypeName}</span>
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveMetrics}
            disabled={isLoading}
            style={{ marginRight: "10px" }}
          >
            {isLoading ? "Saving..." : "Save Setpoints"}
          </Button>
        </div>


        <div className="pmTableHolder" style={{ marginTop: "20px" }}>
          {assetType.componentTypeTables && assetType.componentTypeTables.length > 0 ? (
            assetType.componentTypeTables.map((componentTypeTable, index) => (
              <div key={index}>
                {createMetricsTable(componentTypeTable)}
              </div>
            ))
          ) : (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  No Component Types Found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  This asset type doesn't have any component types configured.
                </Typography>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ mms }) => ({
  currentDepartment: mms.currentDepartment,
});

const mapDispatchToProps = {
  setPageTitle,
};

export default connect(mapStateToProps, mapDispatchToProps)(AssetTypeDetails);
